import { useEffect, useRef, useState, useCallback } from 'react';
import apiService from '../services/apiService';
import { getCustomerLabel } from '../utils/customerCache';
import { getFeatures } from '../utils/featuresCache';

// #48: Für Kommentar-Benachrichtigungen den Text des neuesten Kommentars
// nachladen — läuft nur für frisch gemeldete Tickets, also selten.
const fetchLatestCommentPreview = async (ticketId) => {
  try {
    const resp = await apiService.getTicketHistory(ticketId);
    const comments = (resp?.history || []).filter(h => h?.type === 'comment' && h?.content);
    if (comments.length === 0) return null;
    comments.sort((a, b) => new Date(b.sortTimestamp || b.timestamp) - new Date(a.sortTimestamp || a.timestamp));
    const text = String(comments[0].content).replace(/\s+/g, ' ').trim();
    if (!text) return null;
    return text.length > 140 ? `${text.slice(0, 137)}…` : text;
  } catch (_) {
    return null;
  }
};

const showDesktopNotification = async (ticket) => {
  try {
    if (typeof window === 'undefined' || !('Notification' in window)) return;
    // #16 B6: Ist Web Push aktiv (pushManager), übernimmt der Server-Worker
    // die Desktop-Benachrichtigung — sonst käme sie doppelt an.
    // Glocke + Ton (Header) bleiben unverändert clientseitig.
    if (window.__pushNotificationsActive === true) return;
    // Debug info for troubleshooting desktop notifications
    try {
      // eslint-disable-next-line no-console
      console.log('[NOTIFY] Attempting desktop notification', {
        permission: window.Notification?.permission,
        ticketId: ticket?.id,
        ticketNumber: ticket?.ticketNumber,
        title: ticket?.title
      });
    } catch (_) {}
    // #48: konkrete Aktivität benennen statt pauschal "Neue Aktivität"
    let title;
    let commentPreview = null;
    if (ticket?.isNewTicket || ticket?.isNewTicketForAssignee) {
      title = 'Neues Ticket';
    } else if (ticket?.hasNewComments || ticket?.hasNewCommentsForAssignee) {
      title = 'Neuer Kommentar im Ticket';
      commentPreview = await fetchLatestCommentPreview(ticket.id);
    } else if (ticket?.hasNewEmails || ticket?.hasNewEmailsForAssignee) {
      title = 'E-Mail im Ticket eingegangen';
    } else if (ticket?.hasNewAttachments || ticket?.hasNewAttachmentsForAssignee) {
      title = 'Neuer Anhang im Ticket';
    } else {
      title = 'Neue Aktivität im Ticket';
    }
    // #153: Kundenname (Kurzform) mit anzeigen — nur bei zugeordnetem Kunden
    // und aktivem Kunden-Feature (gleiche Logik wie CustomerLine, #90/#102).
    let customerLabel = null;
    try {
      const features = await getFeatures();
      if (features.customers !== false) customerLabel = await getCustomerLabel(ticket?.customerId);
    } catch (_) {}
    const bodyParts = [];
    if (ticket?.ticketNumber) bodyParts.push(`#${ticket.ticketNumber}`);
    if (customerLabel) bodyParts.push(customerLabel);
    if (ticket?.title) bodyParts.push(ticket.title);
    let body = bodyParts.join(' – ');
    if (commentPreview) body = body ? `${body}\n${commentPreview}` : commentPreview;

    const options = {
      body,
      icon: '/icon-192.png',
      badge: '/icon-192.png',
      requireInteraction: true,
      silent: true,
      // #142: der notificationclick-Handler im Service Worker (sw.js) öffnet
      // damit das zugehörige Ticket.
      data: { ticketId: ticket?.id || null }
    };

    const doShow = async () => {
      try {
        // Prefer service worker notification if available (more consistent on some Linux DEs)
        if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
          const reg = await navigator.serviceWorker.getRegistration();
          if (reg && reg.showNotification) {
            // eslint-disable-next-line no-console
            try { console.log('[NOTIFY] Using ServiceWorkerRegistration.showNotification'); } catch (_) {}
            await reg.showNotification(title, options);
            return;
          }
        }
      } catch (_) {}

      const n = new Notification(title, options);
      n.onclick = () => {
        try { window.focus(); } catch (_) {}
        // #142: Ticket direkt im laufenden Fenster öffnen (App.js-Listener)
        // statt pauschal aufs Dashboard zu springen.
        try {
          if (ticket?.id) {
            window.dispatchEvent(new CustomEvent('openTicketDetailById', { detail: { ticketId: ticket.id } }));
          } else {
            window.location.href = '/dashboard';
          }
        } catch (_) {}
        try { n.close(); } catch (_) {}
      };
    };

    if (Notification.permission === 'granted') {
      doShow();
    } else if (Notification.permission === 'default') {
      Notification.requestPermission().then((perm) => {
        if (perm === 'granted') doShow();
      }).catch(() => {});
    }
  } catch (_) {
    // ignore
  }
};

const useAutoCache = (options = {}) => {
  const {
    type = 'events', // 'events', 'emails', or 'tickets'
    startDate = null,
    endDate = null,
    enabled = true,
    onDataUpdate = null, // Callback function when data is updated
    checkInterval = 60000, // Check every 60 seconds for live data (reduced from 30s to prevent pool exhaustion)
    ticketsFilters = null // Optional filters for tickets (e.g., { includeArchived: '1' })
  } = options;

  const intervalRef = useRef(null);
  const isRefreshingRef = useRef(false);
  const errorCountRef = useRef(0);
  const lastSuccessRef = useRef(Date.now());

  // #256: Der Fehlerzaehler lag bisher ausschliesslich in einem Ref — Aenderungen
  // daran loesen KEIN Neurendern aus. Die Anzeige "Pausiert" im Spaltenkopf
  // erschien deshalb nur zufaellig, naemlich wenn die Komponente aus anderem
  // Grund neu rendert. Fuer einen verlaesslichen Ladezustand wird derselbe Wert
  // zusaetzlich in State gespiegelt; das Ref bleibt die Arbeitskopie fuer die
  // Callbacks (die sonst alle neu gebaut werden muessten).
  // status: 'loading' nur bis zum ERSTEN Ergebnis — danach 'ready' oder 'error',
  // damit nicht bei jeder periodischen Aktualisierung ein Ladekreis aufblitzt.
  const [loadState, setLoadState] = useState(() => ({ status: 'loading', errorCount: 0 }));

  const markLoadSuccess = useCallback(() => {
    errorCountRef.current = 0;
    lastSuccessRef.current = Date.now();
    setLoadState(prev => (prev.status === 'ready' && prev.errorCount === 0)
      ? prev
      : { status: 'ready', errorCount: 0 });
  }, []);

  const markLoadFailure = useCallback(() => {
    errorCountRef.current += 1;
    const anzahl = errorCountRef.current;
    setLoadState(prev => (prev.status === 'error' && prev.errorCount === anzahl)
      ? prev
      : { status: 'error', errorCount: anzahl });
    return anzahl;
  }, []);

  // Zaehler zuruecksetzen, ohne einen Erfolg zu behaupten (Token-Refresh,
  // Start eines manuellen Abrufs): der Status bleibt, wie er ist.
  const clearErrorCount = useCallback(() => {
    errorCountRef.current = 0;
    setLoadState(prev => prev.errorCount === 0 ? prev : { ...prev, errorCount: 0 });
  }, []);
  // Track last requested ticket filter snapshot to avoid applying stale results
  const lastTicketsFiltersRef = useRef(null);

  // NEW: Track current user email (for tickets attention logic)
  const userEmailRef = useRef(null);
  // NEW: Track set of ticket IDs currently requiring attention, to detect new ones
  const attentionIdsRef = useRef(new Set());

  // Configuration for live data
  const LIVE_CONFIG = {
    EMAIL_RANGE_DAYS: 7, // E-Mails der letzten 7 Tage
    EVENT_RANGE_MONTHS: 2, // Events +/- 1 Monat
    REFRESH_INTERVAL: 30 * 1000, // Aktualisiere alle 30 Sekunden
    MAX_CONSECUTIVE_ERRORS: 5, // Stoppe nach 5 aufeinanderfolgenden Fehlern
    ERROR_BACKOFF_BASE: 2000, // Basis für exponential backoff (2 Sekunden)
    MAX_BACKOFF: 60000, // Maximaler Backoff (60 Sekunden)
    HEALTH_CHECK_INTERVAL: 300000 // Alle 5 Minuten prüfen ob Service wieder verfügbar ist
  };

  const ensureUserEmailLoaded = useCallback(async () => {
    if (type !== 'tickets') return null;
    if (userEmailRef.current) return userEmailRef.current;
    try {
      const response = await apiService.getCurrentUser();
      const email = typeof response === 'string'
        ? response
        : (response?.userPrincipalName || response?.mail || response?.email || response?.user?.mail || response?.user?.userPrincipalName);
      userEmailRef.current = email || null;
      return userEmailRef.current;
    } catch (err) {
      console.warn('[LIVE DATA] Could not load current user for attention logic:', err?.message || err);
      userEmailRef.current = null;
      return null;
    }
  }, [type]);

  // Note: sound playback is handled by the header when bell transitions to red

  // Dedup notifications across remounts and quick successive updates
  const NOTIFICATION_DEDUP_MS = 15000; // 15 seconds
  const getRecentlyNotifiedMap = () => {
    try {
      if (typeof window === 'undefined' || !window.localStorage) return {};
      const raw = window.localStorage.getItem('recentlyNotifiedTicketIds');
      const parsed = raw ? JSON.parse(raw) : {};
      if (parsed && typeof parsed === 'object') return parsed;
    } catch (_) {}
    return {};
  };
  const setRecentlyNotifiedMap = (mapObj) => {
    try {
      if (typeof window === 'undefined' || !window.localStorage) return;
      window.localStorage.setItem('recentlyNotifiedTicketIds', JSON.stringify(mapObj));
    } catch (_) {}
  };

  // Persisted flags: notify each ticket only once while it requires attention
  const getPersistentNotifiedFlags = () => {
    try {
      if (typeof window === 'undefined' || !window.localStorage) return {};
      const raw = window.localStorage.getItem('notifiedTicketsUntilCleared');
      const parsed = raw ? JSON.parse(raw) : {};
      if (parsed && typeof parsed === 'object') return parsed;
    } catch (_) {}
    return {};
  };
  const setPersistentNotifiedFlags = (mapObj) => {
    try {
      if (typeof window === 'undefined' || !window.localStorage) return;
      window.localStorage.setItem('notifiedTicketsUntilCleared', JSON.stringify(mapObj));
    } catch (_) {}
  };

  const computeAttentionAndNotify = useCallback(async (ticketData, triggerSoundOnNew = true) => {
    if (type !== 'tickets') return;
    await ensureUserEmailLoaded();
    const currentUserEmail = userEmailRef.current;

    const normalize = (s) => (s || '').trim();
    const equalsCi = (a, b) => !!a && !!b && a.localeCompare(b, undefined, { sensitivity: 'base' }) === 0;

    const currentAttentionIds = new Set();
    const currentAttentionIdStrs = new Set();
    const attentionTicketsMeta = [];

    for (const t of ticketData) {
      const assignedTo = normalize(t.assignedTo);
      const isUnassigned = !assignedTo;
      const isAssignedToMe = currentUserEmail && assignedTo && equalsCi(assignedTo, currentUserEmail);

      const hasNewForAssignee = !!(t.hasNewActivityForAssignee || t.hasNewEmailsForAssignee || t.hasNewCommentsForAssignee || t.hasNewAttachmentsForAssignee || t.isNewTicketForAssignee);
      const hasNewGlobal = !!(t.hasNewActivity || t.hasNewEmails || t.hasNewComments || t.hasNewAttachments || t.isNewTicket);

      // W0-6 (Beschluss 56): abgeschlossene Tickets melden sich nicht mehr in
      // der Glocke — neue Aktivität führt über Reopen/Vorgang zu einem offenen
      // Ticket, das dann regulär gemeldet wird.
      const requiresAttention = t.status !== 'abgeschlossen' &&
        ((isUnassigned && hasNewGlobal) || (isAssignedToMe && hasNewForAssignee));
      if (requiresAttention) {
        currentAttentionIds.add(t.id);
        currentAttentionIdStrs.add(String(t.id));
        // #126: konkrete Aktivität auch für die Glocke — gleiche Präzedenz
        // wie die Desktop-Benachrichtigung (#48, showDesktopNotification).
        const activityType = (t.isNewTicket || t.isNewTicketForAssignee) ? 'newTicket'
          : (t.hasNewComments || t.hasNewCommentsForAssignee) ? 'comment'
          : (t.hasNewEmails || t.hasNewEmailsForAssignee) ? 'email'
          : (t.hasNewAttachments || t.hasNewAttachmentsForAssignee) ? 'attachment'
          : 'activity';
        attentionTicketsMeta.push({
          id: t.id,
          ticketNumber: t.ticketNumber,
          title: t.title,
          status: t.status,
          priority: t.priority,
          assignedTo: t.assignedTo,
          isUnassigned,
          isAssignedToMe,
          hasNewForAssignee,
          hasNewGlobal,
          activityType,
          // #153: Kunde auch in der Glocke (Header rendert CalendarTicketTitle)
          customerId: t.customerId || null
        });
      }
    }

    // Compare with previous to find new attention tickets
    const prev = attentionIdsRef.current || new Set();
    const newlyAdded = [];
    currentAttentionIds.forEach((id) => {
      if (!prev.has(id)) newlyAdded.push(id);
    });

    // Persist current set
    attentionIdsRef.current = currentAttentionIds;

    // Dispatch global event for header bell
    try {
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('ticketsAttentionUpdate', {
          detail: {
            unreadCount: currentAttentionIds.size,
            hasUnread: currentAttentionIds.size > 0,
            tickets: attentionTicketsMeta
          }
        }));
      }
    } catch (_) {}

    // Desktop notifications and sound for newly added
    if (newlyAdded.length > 0) {
      const byId = new Map(ticketData.map(t => [t.id, t]));
      const now = Date.now();
      const shortCache = getRecentlyNotifiedMap();
      // prune expired short-term cache
      Object.keys(shortCache).forEach((key) => {
        const ts = Number(shortCache[key] || 0);
        if (!ts || (now - ts) > NOTIFICATION_DEDUP_MS) {
          delete shortCache[key];
        }
      });

      // long-lived flags across remounts
      const persistentFlags = getPersistentNotifiedFlags();
      // remove flags for tickets that no longer require attention
      Object.keys(persistentFlags).forEach((ticketIdKey) => {
        if (!currentAttentionIdStrs.has(ticketIdKey)) delete persistentFlags[ticketIdKey];
      });

      newlyAdded.forEach((id) => {
        const t = byId.get(id);
        if (!t) return;
        const idKey = String(id);
        // Skip if we've already notified for this ticket while it still requires attention
        if (persistentFlags[idKey]) return;
        // Also skip rapid duplicates within a very short time window
        const lastTs = Number(shortCache[idKey] || 0);
        if (lastTs && (now - lastTs) < NOTIFICATION_DEDUP_MS) return;

        shortCache[idKey] = now;
        persistentFlags[idKey] = true;
        setRecentlyNotifiedMap(shortCache);
        setPersistentNotifiedFlags(persistentFlags);
        showDesktopNotification(t);
      });
    }
  }, [type, ensureUserEmailLoaded]);

  const checkAndRefreshCache = useCallback(async () => {
    if (!enabled || isRefreshingRef.current) {
      return;
    }

    // Check if we should suspend due to too many errors
    if (errorCountRef.current >= LIVE_CONFIG.MAX_CONSECUTIVE_ERRORS) {
      const timeSinceLastSuccess = Date.now() - lastSuccessRef.current;
      
      // Only try health check every 5 minutes
      if (timeSinceLastSuccess < LIVE_CONFIG.HEALTH_CHECK_INTERVAL) {
        console.log(`🚫 [LIVE DATA] ${type} suspended due to errors, waiting for next health check...`);
        return;
      }
      
      console.log(`🔍 [LIVE DATA] ${type} health check after suspension...`);
    }

    try {
      console.log(`🔍 [LIVE DATA] Loading ${type} LIVE data...`);
      
      if (type === 'emails') {
        console.log(`📧 [LIVE DATA] Loading LIVE emails (last ${LIVE_CONFIG.EMAIL_RANGE_DAYS} days)...`);
        try {
          const response = await apiService.getEmails();
          const emailData = response.value || [];
          
          console.log(`✅ [LIVE DATA] LIVE emails loaded (${emailData.length} items, live: ${response.live})`);
          
          markLoadSuccess();
          
          if (onDataUpdate) {
            onDataUpdate(emailData);
          }
          
          // Add browser console debug for live emails
          if (typeof window !== 'undefined' && window.console) {
            window.console.log(`📧 [BROWSER] LIVE emails loaded: ${emailData.length} emails`);
          }
          
        } catch (emailError) {
          markLoadFailure();
          console.warn(`❌ [LIVE DATA] LIVE email load failed (${errorCountRef.current}/${LIVE_CONFIG.MAX_CONSECUTIVE_ERRORS}):`, emailError.message);
          
          if (errorCountRef.current >= LIVE_CONFIG.MAX_CONSECUTIVE_ERRORS) {
            console.error(`🚫 [LIVE DATA] Suspending ${type} auto-refresh due to consecutive errors`);
          }
        }
      } else if (type === 'events') {
        // For events, always load live data (1 month before/after)
        console.log(`📅 [LIVE DATA] Loading LIVE events (±1 month)...`);
        try {
          const response = await apiService.getEvents(startDate, endDate);
          const eventData = response.value || [];
          
          console.log(`✅ [LIVE DATA] LIVE events loaded (${eventData.length} items, live: ${response.live})`);
          
          markLoadSuccess();
          
          if (onDataUpdate) {
            onDataUpdate(eventData);
          }
          
          // Add browser console debug for live events
          if (typeof window !== 'undefined' && window.console) {
            window.console.log(`📅 [BROWSER] LIVE events loaded: ${eventData.length} events`);
          }
          
        } catch (eventError) {
          markLoadFailure();
          console.warn(`❌ [LIVE DATA] LIVE event load failed (${errorCountRef.current}/${LIVE_CONFIG.MAX_CONSECUTIVE_ERRORS}):`, eventError.message);
          
          if (errorCountRef.current >= LIVE_CONFIG.MAX_CONSECUTIVE_ERRORS) {
            console.error(`🚫 [LIVE DATA] Suspending ${type} auto-refresh due to consecutive errors`);
          }
        }
      } else if (type === 'tickets') {
        // For tickets, load with optional filters (e.g., includeArchived)
        const currentFilters = ticketsFilters || {};
        lastTicketsFiltersRef.current = JSON.stringify(currentFilters);
        console.log(`🎫 [LIVE DATA] Loading ticket data...`, currentFilters && Object.keys(currentFilters).length ? { filters: currentFilters } : undefined);
        try {
          const response = await apiService.getTickets(currentFilters);
          const ticketData = response.tickets || [];
          // Drop stale responses that do not match the most recent filters
          if (lastTicketsFiltersRef.current !== JSON.stringify(currentFilters)) {
            console.log('⚠️ [LIVE DATA] Dropping stale tickets response due to filter change');
            return;
          }
          
          console.log(`✅ [LIVE DATA] Tickets loaded (${ticketData.length} items)`);
          
          markLoadSuccess();
          
          if (onDataUpdate) {
            onDataUpdate(ticketData);
          }

          // NEW: compute attention + notify + dispatch
          await computeAttentionAndNotify(ticketData, true);
        } catch (ticketError) {
          markLoadFailure();
          console.warn(`❌ [LIVE DATA] Ticket load failed (${errorCountRef.current}/${LIVE_CONFIG.MAX_CONSECUTIVE_ERRORS}):`, ticketError.message);
          
          if (errorCountRef.current >= LIVE_CONFIG.MAX_CONSECUTIVE_ERRORS) {
            console.error(`🚫 [LIVE DATA] Suspending ${type} auto-refresh due to consecutive errors`);
          }
        }
      }
    } catch (error) {
      markLoadFailure();
      console.error(`❌ [LIVE DATA] Error loading ${type} data (${errorCountRef.current}/${LIVE_CONFIG.MAX_CONSECUTIVE_ERRORS}):`, error);
      
      if (errorCountRef.current >= LIVE_CONFIG.MAX_CONSECUTIVE_ERRORS) {
        console.error(`🚫 [LIVE DATA] Suspending ${type} auto-refresh due to consecutive errors`);
      }
    }
  }, [type, enabled, startDate, endDate, onDataUpdate, computeAttentionAndNotify, ticketsFilters, markLoadSuccess, markLoadFailure]);

  const refreshData = useCallback(async () => {
    if (isRefreshingRef.current) {
      console.log(`🔄 [LIVE DATA] ${type} refresh already in progress, skipping...`);
      return;
    }

    isRefreshingRef.current = true;
    
    try {
      console.log(`🔄 [LIVE DATA] Refreshing ${type} LIVE data...`);
      
      let newData;
      if (type === 'events') {
        const response = await apiService.getEvents(startDate, endDate);
        newData = response.value || [];
      } else if (type === 'emails') {
        const response = await apiService.getEmails();
        newData = response.value || [];
      } else if (type === 'tickets') {
        const currentFilters = ticketsFilters || {};
        lastTicketsFiltersRef.current = JSON.stringify(currentFilters);
        const response = await apiService.getTickets(currentFilters);
        // Drop stale responses that do not match the most recent filters
        if (lastTicketsFiltersRef.current !== JSON.stringify(currentFilters)) {
          console.log('⚠️ [LIVE DATA] Dropping stale tickets response (manual refresh) due to filter change');
          return [];
        }
        newData = response.tickets || [];
      }

      console.log(`✅ [LIVE DATA] ${type} LIVE data refreshed:`, {
        count: newData?.length || 0,
        live: true
      });

      markLoadSuccess();

      // Add browser console debug for refresh
      if (typeof window !== 'undefined' && window.console) {
        window.console.log(`🔄 [BROWSER] ${type} LIVE data refreshed: ${newData?.length || 0} items`);
      }

      // Notify parent component about new data
      if (onDataUpdate && newData) {
        onDataUpdate(newData);
      }

      // NEW: compute attention + notify + dispatch also for manual refresh (tickets)
      if (type === 'tickets' && newData) {
        await computeAttentionAndNotify(newData, false);
      }
    } catch (error) {
      markLoadFailure();
      console.error(`❌ [LIVE DATA] Error refreshing ${type} data (${errorCountRef.current}/${LIVE_CONFIG.MAX_CONSECUTIVE_ERRORS}):`, error);
    } finally {
      isRefreshingRef.current = false;
    }
  }, [type, startDate, endDate, onDataUpdate, computeAttentionAndNotify, ticketsFilters, markLoadSuccess, markLoadFailure]);

  // Calculate dynamic interval based on error count (exponential backoff)
  const getDynamicInterval = useCallback(() => {
    if (errorCountRef.current === 0) {
      return checkInterval;
    }
    
    const backoff = Math.min(
      LIVE_CONFIG.ERROR_BACKOFF_BASE * Math.pow(2, Math.max(0, errorCountRef.current - 1)),
      LIVE_CONFIG.MAX_BACKOFF
    );
    return Math.max(checkInterval, backoff);
  }, [checkInterval]);

  useEffect(() => {
    if (!enabled) return;

    // Initial load immediately
    checkAndRefreshCache();

    // Setup periodic refresh
    const startInterval = () => {
      const interval = setInterval(() => {
        checkAndRefreshCache();
      }, getDynamicInterval());
      intervalRef.current = interval;
    };

    startInterval();

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [enabled, checkInterval, getDynamicInterval, checkAndRefreshCache]);

  // Reset error count (useful after successful token refresh)
  const resetErrorCount = useCallback(() => {
    if (errorCountRef.current > 0) {
      console.log(`🔄 [LIVE DATA] Resetting error count for ${type} (was: ${errorCountRef.current})`);
      clearErrorCount();
      lastSuccessRef.current = Date.now();
    }
  }, [type, clearErrorCount]);

  // Listen for successful token refresh events to reset error count
  useEffect(() => {
    const handleTokenRefreshSuccess = () => {
      console.log(`🔄 [TOKEN REFRESH] Token refresh successful, resetting ${type} error count`);
      resetErrorCount();
      
      // Immediately try to reload data if we were suspended
      if (errorCountRef.current >= LIVE_CONFIG.MAX_CONSECUTIVE_ERRORS) {
        console.log(`🔄 [TOKEN REFRESH] Service was suspended, attempting immediate reload for ${type}`);
        setTimeout(() => {
          checkAndRefreshCache();
        }, 1000); // Small delay to ensure token is fully updated
      }
    };

    const handleAutomaticRelogin = () => {
      console.log(`🔄 [AUTO RELOGIN] Automatic relogin started, stopping ${type} auto-refresh`);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };

    // Check if we can add event listeners
    if (typeof window !== 'undefined') {
      window.addEventListener('tokenRefreshSuccess', handleTokenRefreshSuccess);
      window.addEventListener('automaticReloginStarted', handleAutomaticRelogin);
      
      return () => {
        window.removeEventListener('tokenRefreshSuccess', handleTokenRefreshSuccess);
        window.removeEventListener('automaticReloginStarted', handleAutomaticRelogin);
      };
    }
  }, [type, resetErrorCount, checkAndRefreshCache]);

  // Manual refresh function
  const manualRefresh = useCallback(() => {
    console.log(`🔄 [LIVE DATA] Manual refresh triggered for ${type}`);
    
    // Reset error count on manual refresh
    clearErrorCount();
    
    // Add browser console debug for manual refresh
    if (typeof window !== 'undefined' && window.console) {
      window.console.log(`🔄 [BROWSER] Manual refresh triggered for ${type}`);
    }
    
    return refreshData();
  }, [refreshData, type, clearErrorCount]);

  return {
    manualRefresh,
    // #256: aus dem State, nicht aus dem Ref — nur so sieht die Oberflaeche
    // eine Aenderung. 'loading' gilt bis zum ersten Ergebnis, danach
    // 'ready' oder 'error'.
    status: loadState.status,
    errorCount: loadState.errorCount,
    isSuspended: loadState.errorCount >= LIVE_CONFIG.MAX_CONSECUTIVE_ERRORS
  };
};

export default useAutoCache; 